import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";

export const HiddenPurchasedFieldsDialog = ({ fieldNames = [], onClose, open }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEnforceFocus>
    <DialogContent>
      <h3 className="dialog-title">Show a purchased field to regenerate</h3>
      <div className="regeneration-dialog-description">
        The purchased badge data matches fields in the current design, but all
        matching fields are hidden. Make at least one of these fields visible on
        the badge before regenerating:
      </div>
      <ul className="regeneration-field-list">
        {fieldNames.map((fieldName) => (
          <li key={fieldName}>{fieldName}</li>
        ))}
      </ul>
    </DialogContent>
    <DialogActions>
      <button className="primary" onClick={onClose} type="button">
        Close
      </button>
    </DialogActions>
  </Dialog>
);

export const PurchasedFieldsMappingDialog = ({
  attendees = [],
  currentFieldNames = [],
  onClose,
  onSubmit,
  open,
  purchasedFieldNames = [],
}) => {
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState("");
  const errorRef = useRef(null);
  const currentFieldsKey = currentFieldNames.join("\u0000");
  const purchasedFieldsKey = purchasedFieldNames.join("\u0000");
  const stableCurrentFieldNames = useMemo(
    () => currentFieldsKey.split("\u0000").filter(Boolean),
    [currentFieldsKey]
  );
  const stablePurchasedFieldNames = useMemo(
    () => purchasedFieldsKey.split("\u0000").filter(Boolean),
    [purchasedFieldsKey]
  );

  useEffect(() => {
    if (open) {
      setMapping(
        stablePurchasedFieldNames.reduce((result, fieldName) => {
          result[fieldName] = "";
          return result;
        }, {})
      );
      setError("");
    }
  }, [open, stablePurchasedFieldNames]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  const handleMappingChange = (purchasedFieldName, currentFieldName) => {
    setMapping((currentMapping) => ({
      ...currentMapping,
      [purchasedFieldName]: currentFieldName,
    }));
    setError("");
  };

  const getAvailableCurrentFields = (purchasedFieldName) => {
    const usedFields = Object.entries(mapping)
      .filter(
        ([sourceFieldName, targetFieldName]) =>
          sourceFieldName !== purchasedFieldName && targetFieldName
      )
      .map(([, targetFieldName]) => targetFieldName);
    return stableCurrentFieldNames.filter(
      (fieldName) => !usedFields.includes(fieldName)
    );
  };

  const handleSubmit = () => {
    const selectedMapping = Object.fromEntries(
      Object.entries(mapping).filter(([, currentFieldName]) => currentFieldName)
    );
    if (!Object.keys(selectedMapping).length) {
      setError(
        stableCurrentFieldNames.length
          ? "Map at least one purchased field to a visible field in the current design."
          : "Make at least one field visible in the current design before mapping purchased data."
      );
      return;
    }
    onSubmit(selectedMapping);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      classes={{ root: "mapping-dialog-root" }}
    >
      <DialogContent>
        <h3 className="dialog-title">Map purchased data to the current design</h3>
        <div className="mapping-description">
          The purchased badge fields no longer exist in the current design.
          Map at least one purchased field to a visible design field to continue.
        </div>
        {error && (
          <div className="mapping-error" ref={errorRef} role="alert">
            {error}
          </div>
        )}
        <div className="mapping-columns-container purchased-regeneration-mapping-columns">
          {stablePurchasedFieldNames.map((purchasedFieldName) => {
            const currentValue = mapping[purchasedFieldName] || "";
            const availableFields = getAvailableCurrentFields(purchasedFieldName);
            const options =
              currentValue && !availableFields.includes(currentValue)
                ? [currentValue, ...availableFields]
                : availableFields;
            const previewValues = attendees
              .slice(0, 3)
              .map((attendee) => attendee?.[purchasedFieldName] ?? "");

            return (
              <div className="mapping-column-card" key={purchasedFieldName}>
                <div className="mapping-column-preview">
                  <div className="mapping-column-header">
                    {purchasedFieldName}
                  </div>
                  {previewValues.map((value, index) => (
                    <div className="mapping-data-row" key={index}>
                      {String(value || "—")}
                    </div>
                  ))}
                </div>
                <div className="mapping-arrow-section">
                  <span className="mapping-to-label">to</span>
                  <TextField
                    select
                    size="small"
                    value={currentValue}
                    onChange={(event) =>
                      handleMappingChange(
                        purchasedFieldName,
                        event.target.value
                      )
                    }
                    SelectProps={{ native: true }}
                    variant="outlined"
                    className={`mapping-select ${
                      currentValue ? "mapping-select-bold" : ""
                    }`}
                  >
                    <option value="">Do not use</option>
                    {options.map((fieldName) => (
                      <option key={fieldName} value={fieldName}>
                        {fieldName}
                      </option>
                    ))}
                  </TextField>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
      <DialogActions className="mapping-dialog-actions">
        <button className="button" onClick={onClose} type="button">
          Cancel
        </button>
        <button className="primary" onClick={handleSubmit} type="button">
          Continue
        </button>
      </DialogActions>
    </Dialog>
  );
};
